import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import SalaryInput from '../salaryCalculator/Inputs/SalaryCalculator/SalaryInput';
import OvertimeInput from '../salaryCalculator/Inputs/SalaryCalculator/OvertimeInput';
import PensionInput from '../salaryCalculator/Inputs/SalaryCalculator/PensionInput';
import StudentLoanInput from '../salaryCalculator/Inputs/SalaryCalculator/StudentLoanInput';
import YearSelector from '../salaryCalculator/Inputs/SalaryCalculator/YearSelector';
import OptionsToggleButton from '../salaryCalculator/Inputs/SalaryCalculator/OptionsToggleButton';
import ScottishResidentSelector from '../salaryCalculator/Inputs/SalaryCalculator/ScottishResident';
import { useConfig } from '../../context/ConfigContext';
import { lightColours } from '../../styles/colours';

const ComparisonInputs = ({ onInputChange }) => {
  const { config } = useConfig();

  // Default input structure
  const defaultInput = {
    salary: 40000,
    overtime: { include: false, value: 0, inputType: 'amount' },
    pension: {
      active: false,
      contribution: 5,
      inputType: 'percentage',
      scheme: 'Auto-enrolment',
      calculationBasis: 'Qualifying Earnings',
      includeOvertime: false,
    },
    studentLoanPlan: { plan: 'none' }
  };

  // State for both salary scenarios
  const [salaryA, setSalaryA] = useState(defaultInput);
  const [salaryB, setSalaryB] = useState(defaultInput);

  // State for showing additional options
  const [showOptionsA, setShowOptionsA] = useState(false);
  const [showOptionsB, setShowOptionsB] = useState(false);

  // Use ref to store the latest onInputChange to avoid dependency issues
  const onInputChangeRef = useRef(onInputChange);
  onInputChangeRef.current = onInputChange;

  // Notify parent of changes with minimal debouncing
  useEffect(() => {
    // Don't trigger calculations until config is loaded
    if (!config) return;
    
    // Immediate call for initial render, debounced for subsequent changes
    const isInitialRender = salaryA.salary === 40000 && salaryB.salary === 40000;
    
    if (isInitialRender) {
      onInputChangeRef.current({ salaryA, salaryB });
    } else {
      const timeoutId = setTimeout(() => {
        onInputChangeRef.current({ salaryA, salaryB });
      }, 10); // 10ms debounce - just enough to batch rapid changes

      return () => clearTimeout(timeoutId);
    }
  }, [salaryA, salaryB, config]);

  // Handlers for Salary A
  const handleSalaryAChange = (newSalary) => {
    setSalaryA(prev => ({ ...prev, salary: newSalary }));
  };

  const handleOvertimeAChange = (overtimeData) => {
    setSalaryA(prev => ({ ...prev, overtime: overtimeData }));
  };

  const handlePensionAChange = (pensionData) => {
    setSalaryA(prev => ({ ...prev, pension: pensionData }));
  };

  const handleStudentLoanAChange = (studentLoanData) => {
    setSalaryA(prev => ({ ...prev, studentLoanPlan: studentLoanData }));
  };

  // Handlers for Salary B
  const handleSalaryBChange = (newSalary) => {
    setSalaryB(prev => ({ ...prev, salary: newSalary }));
  };

  const handleOvertimeBChange = (overtimeData) => {
    setSalaryB(prev => ({ ...prev, overtime: overtimeData }));
  };

  const handlePensionBChange = (pensionData) => {
    setSalaryB(prev => ({ ...prev, pension: pensionData }));
  };

  const handleStudentLoanBChange = (studentLoanData) => {
    setSalaryB(prev => ({ ...prev, studentLoanPlan: studentLoanData }));
  };

  // Don't render until config is loaded
  if (!config) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h4" component="h2" gutterBottom sx={{ 
        textAlign: 'center',
        fontWeight: 'bold',
        mb: 4
      }}>
        Compare Two Salaries
      </Typography>

      {/* Tax Year Selection */}
      <Box sx={{ maxWidth: '400px', mx: 'auto', mb: 4 }}>
        <YearSelector />
      </Box>

      {/* Test: Add back minimal flexbox layout */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        gap: 4
      }}>
        <Box sx={{ 
          flex: 1,
          p: 3, 
          border: '1px solid #e0e0e0', 
          borderRadius: 2,
          backgroundColor: '#fff'
        }}>
          <Typography variant="h5" component="h3" gutterBottom sx={{ 
            fontWeight: 'bold',
            color: lightColours.darkBlue,
            textAlign: 'center',
            mb: 3
          }}>
            Current Salary
          </Typography>
          
          <SalaryInput
            salary={salaryA.salary}
            onSalaryChange={handleSalaryAChange}
            config={config}
          />

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <OptionsToggleButton
              showMoreOptions={showOptionsA}
              setShowMoreOptions={setShowOptionsA}
            />
          </Box>

          {showOptionsA && (
            <Box sx={{ mt: 3 }}>
              {/* Scottish Residency Section */}
              <Box sx={{ mb: 3 }}>
                <ScottishResidentSelector />
              </Box>
              
              <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

              {/* Overtime Section */}
              <Box sx={{ mb: 3 }}>
                <OvertimeInput
                  overtimeValue={salaryA.overtime.value}
                  onOvertimeValueChange={(value) => handleOvertimeAChange({ 
                    include: value > 0, 
                    value: value, 
                    inputType: 'amount' 
                  })}
                  salary={salaryA.salary}
                  config={config}
                />
              </Box>
              
              <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

              {/* Pension Section */}
              <Box sx={{ mb: 3 }}>
                <PensionInput
                  pensionContribution={salaryA.pension.contribution}
                  pensionInputType={salaryA.pension.inputType}
                  pensionScheme={salaryA.pension.scheme}
                  pensionCalculationBasis={salaryA.pension.calculationBasis}
                  onPensionContributionChange={(contribution) => handlePensionAChange({
                    ...salaryA.pension,
                    active: contribution > 0,
                    contribution: contribution
                  })}
                  onPensionInputTypeChange={(inputType) => handlePensionAChange({
                    ...salaryA.pension,
                    inputType: inputType
                  })}
                  onPensionSchemeChange={(scheme) => handlePensionAChange({
                    ...salaryA.pension,
                    scheme: scheme
                  })}
                  onPensionCalculationBasisChange={(basis) => handlePensionAChange({
                    ...salaryA.pension,
                    calculationBasis: basis
                  })}
                  salary={salaryA.salary}
                  includeOvertime={salaryA.pension.includeOvertime}
                  onIncludeOvertimeChange={(include) => handlePensionAChange({
                    ...salaryA.pension,
                    includeOvertime: include
                  })}
                />
              </Box>
              
              <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

              {/* Student Loan Section */}
              <Box sx={{ mb: 3 }}>
                <StudentLoanInput
                  studentLoanPlan={salaryA.studentLoanPlan.plan}
                  onStudentLoanPlanChange={(plan) => handleStudentLoanAChange({
                    plan: plan
                  })}
                />
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ 
          flex: 1,
          p: 3, 
          border: '1px solid #e0e0e0', 
          borderRadius: 2,
          backgroundColor: '#fff'
        }}>
          <Typography variant="h5" component="h3" gutterBottom sx={{ 
            fontWeight: 'bold',
            color: lightColours.darkBlue,
            textAlign: 'center',
            mb: 3
          }}>
            New Salary
          </Typography>
          
          <SalaryInput
            salary={salaryB.salary}
            onSalaryChange={handleSalaryBChange}
            config={config}
          />

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <OptionsToggleButton
              showMoreOptions={showOptionsB}
              setShowMoreOptions={setShowOptionsB}
            />
          </Box>

          {showOptionsB && (
            <Box sx={{ mt: 3 }}>
              {/* Scottish Residency Section */}
              <Box sx={{ mb: 3 }}>
                <ScottishResidentSelector />
              </Box>
              
              <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

              {/* Overtime Section */}
              <Box sx={{ mb: 3 }}>
                <OvertimeInput
                  overtimeValue={salaryB.overtime.value}
                  onOvertimeValueChange={(value) => handleOvertimeBChange({ 
                    include: value > 0, 
                    value: value, 
                    inputType: 'amount' 
                  })}
                  salary={salaryB.salary}
                  config={config}
                />
              </Box>
              
              <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

              {/* Pension Section */}
              <Box sx={{ mb: 3 }}>
                <PensionInput
                  pensionContribution={salaryB.pension.contribution}
                  pensionInputType={salaryB.pension.inputType}
                  pensionScheme={salaryB.pension.scheme}
                  pensionCalculationBasis={salaryB.pension.calculationBasis}
                  onPensionContributionChange={(contribution) => handlePensionBChange({
                    ...salaryB.pension,
                    active: contribution > 0,
                    contribution: contribution
                  })}
                  onPensionInputTypeChange={(inputType) => handlePensionBChange({
                    ...salaryB.pension,
                    inputType: inputType
                  })}
                  onPensionSchemeChange={(scheme) => handlePensionBChange({
                    ...salaryB.pension,
                    scheme: scheme
                  })}
                  onPensionCalculationBasisChange={(basis) => handlePensionBChange({
                    ...salaryB.pension,
                    calculationBasis: basis
                  })}
                  salary={salaryB.salary}
                  includeOvertime={salaryB.pension.includeOvertime}
                  onIncludeOvertimeChange={(include) => handlePensionBChange({
                    ...salaryB.pension,
                    includeOvertime: include
                  })}
                />
              </Box>
              
              <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

              {/* Student Loan Section */}
              <Box sx={{ mb: 3 }}>
                <StudentLoanInput
                  studentLoanPlan={salaryB.studentLoanPlan.plan}
                  onStudentLoanPlanChange={(plan) => handleStudentLoanBChange({
                    plan: plan
                  })}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ComparisonInputs;